/**
 * Direct manipulation and context menus of the calendar page (Outlook semantics):
 * - free-time selection (click/drag), then typing, Enter or double-click → quick create; right-click → slot menu;
 * - move/resize by drag or keyboard: recurring → "this occurrence / the whole series", organizer of a meeting with
 *   attendees → "send update?"; optimistic with rollback, Undo in the toast;
 * - event menu: open, edit, respond, copy/duplicate/paste, show as, private, delete (with confirmation).
 */
import { useI18n } from '@platform/i18n';
import { ConfirmDialog, resolveActions, SLOT_CONTEXT_ID, toast, useErrorText, type CalendarGridEvent, type ContextMenuSpec } from '@platform/ui';
import { useEffect, useMemo, useRef, useState, type MouseEvent, type ReactNode } from 'react';
import { createEventActions, createSlotActions, editable, type EventActionHandlers, type NewKind, type SlotTarget } from '../actions/calendar-actions';
import { api, type CalendarEventInput, type CalendarEventScope } from '../api';
import { useCalendar, type EditScope } from './calendar-context';
import { nextHalfHour, type ZoneShift } from './zones';
import { useChoice } from './choice-dialog';
import { isBlock, type ShownEvent } from './events';
import { QuickCreate, type QuickCreateRequest } from './quick-create';
import { MeetingDialogs, type MeetingDialog } from './meeting-dialogs';
import { CategorizeDialog } from './categorize-dialog';
import { useUndoableCalendar } from './undo';
import { startDownload } from '../download';

type Range = { start: Date; end: Date };

/** Next free half hour today (default target of "New event" without a selection). */
function nextSlot(zone: ZoneShift): SlotTarget {
  const start = nextHalfHour(zone);
  return { start, end: new Date(start.getTime() + 30 * 60_000), allDay: false };
}


export function useCalendarInteractions(shown: readonly ShownEvent[], options: { print?: () => void } = {}) {
  const { t } = useI18n();
  const errorText = useErrorText();
  const { setSelectedId, openEditor, changed, setDate, setView, date, view, zone } = useCalendar();
  const undoable = useUndoableCalendar();
  const respond = (item: ShownEvent, response: 'accept' | 'tentative' | 'decline') => void undoable.respond(item.event, response);
  const [choiceDialog, ask] = useChoice();
  // The selection belongs to the shown period and view: another period or view drops it.
  const periodKey = `${view}|${date.getTime()}`;
  const [picked, setPicked] = useState<{ slot: SlotTarget; key: string }>();
  const selection = picked?.key === periodKey ? picked.slot : undefined;
  const setSelection = (slot: SlotTarget | undefined) => setPicked(slot && { slot, key: periodKey });
  const [quick, setQuick] = useState<QuickCreateRequest>();
  const [overrides, setOverrides] = useState<ReadonlyMap<string, Range>>(new Map());
  const [clipboard, setClipboard] = useState<ShownEvent>();
  const [meetingDialog, setMeetingDialog] = useState<MeetingDialog>();
  const [categorizing, setCategorizing] = useState<{ item: ShownEvent; scope: CalendarEventScope }>();
  const [deleting, setDeleting] = useState<{ item: ShownEvent; scope: CalendarEventScope }>();


  const byGridId = (id: string) => shown.find((s) => s.grid.id === id);
  const canDrag = (event: CalendarGridEvent) => {
    const item = byGridId(event.id);
    return Boolean(item && editable(item) && !item.event.allDay);
  };
  const withOverride = (id: string, range: Range | undefined) =>
    setOverrides((current) => {
      const next = new Map(current);
      if (range) next.set(id, range);
      else next.delete(id);
      return next;
    });

  /** Recurring: which part? Undefined = cancelled. */
  function askScope(item: ShownEvent): Promise<CalendarEventScope | undefined>;
  function askScope(item: ShownEvent, following: true): Promise<EditScope | undefined>;
  async function askScope(item: ShownEvent, following = false): Promise<EditScope | undefined> {
    return item.event.isRecurring
      ? ask({
          title: t('webmail.calendar.ask.scopeTitle'),
          description: t('webmail.calendar.ask.scopeDescription', { title: item.grid.title }),
          choices: [
            { value: 'occurrence', label: t('webmail.calendar.scope.occurrence'), primary: true },
            ...(following ? [{ value: 'following' as const, label: t('webmail.calendarUx.recurrence.following') }] : []),
            { value: 'series', label: t('webmail.calendar.scope.series') },
          ],
        })
      : 'occurrence';
  }
  /** Organizer of a meeting with attendees: send the update? Undefined = cancelled. */
  const askNotify = async (item: ShownEvent): Promise<boolean | undefined> => {
    if (!item.event.isMeeting || !item.event.isOrganizer || !item.event.attendees.some((a) => a.type !== 'resource')) return true;
    const answer = await ask({
      title: t('webmail.calendar.ask.notifyTitle'),
      description: t('webmail.calendar.ask.notifyDescription'),
      choices: [
        { value: 'skip', label: t('webmail.calendar.ask.dontSend') },
        { value: 'send', label: t('webmail.calendar.ask.send'), primary: true },
      ],
    });
    return answer === undefined ? undefined : answer === 'send';
  };

  /** Saves new times of an event (drag, resize, keyboard) with optimistic display, rollback and Undo. */
  const changeTimes = async (gridId: string, start: Date, end: Date) => {
    const item = byGridId(gridId);
    if (!item || !editable(item)) return;
    const before = { start: new Date(item.event.start), end: new Date(item.event.end) };
    withOverride(gridId, { start, end });
    const scope = await askScope(item);
    const notify = scope && (await askNotify(item));
    if (!scope || notify === undefined) {
      withOverride(gridId, undefined);
      return;
    }
    const save = (range: Range) => api.updateEvent(item.event.id, { start: range.start.toISOString(), end: range.end.toISOString() }, scope, notify);
    try {
      await save({ start: zone.fromWall(start), end: zone.fromWall(end) });
      toast.success(t('webmail.calendar.movedToast'), {
        action: {
          label: t('common.undo'),
          onClick: () => {
            save(before).then(changed, (error: unknown) => errorText.notify(error));
          },
        },
      });
      changed();
    } catch (error) {
      errorText.notify(error);
    } finally {
      withOverride(gridId, undefined);
    }
  };

  const createFrom = async (source: ShownEvent, range: Range) => {
    try {
      const detail = await api.getEvent(source.event.id);
      const input: CalendarEventInput = {
        subject: detail.subject,
        start: zone.fromWall(range.start).toISOString(),
        end: zone.fromWall(range.end).toISOString(),
        allDay: detail.allDay,
        location: detail.location,
        showAs: detail.showAs,
        sensitivity: detail.sensitivity,
        reminderMinutes: detail.reminderMinutes,
        ...(detail.bodyHtml && { bodyHtml: detail.bodyHtml }),
        timeZone: zone.zone,
      };
      await api.createEvent(source.calendar.canCreate ? source.calendar.id : 'own', input);
      toast.success(t('webmail.calendar.copiedToast'));
      changed();
    } catch (error) {
      errorText.notify(error);
    }
  };

  const update = async (item: ShownEvent, changes: Parameters<typeof api.updateEvent>[1]) => {
    try {
      await api.updateEvent(item.event.id, changes);
      changed();
    } catch (error) {
      errorText.notify(error);
    }
  };

  const pasteAt = (slot: SlotTarget) => {
    if (!clipboard) return;
    const length = clipboard.grid.end.getTime() - clipboard.grid.start.getTime();
    void createFrom(clipboard, { start: slot.start, end: new Date(slot.start.getTime() + length) });
  };

  const slotActions = createSlotActions(
    {
      create: (slot, kind: NewKind) => {
        if (kind === 'allDay') {
          const day = new Date(slot.start.getFullYear(), slot.start.getMonth(), slot.start.getDate());
          openEditor({ mode: 'create', start: day, end: new Date(day.getFullYear(), day.getMonth(), day.getDate() + 1), allDay: true });
        } else openEditor({ mode: 'create', start: slot.start, end: slot.end, allDay: slot.allDay, recurring: kind === 'recurring' });
        setSelection(undefined);
      },
      paste: (slot) => pasteAt(slot),
      today: () => setDate(zone.toWall(new Date())),
      print: () => options.print?.(),
      view: setView,
    },
    { canPaste: clipboard !== undefined },
  );

  const eventActionHandlers: EventActionHandlers = {
    open: (item) => setSelectedId(item.grid.id),
    edit: (item) => {
      void (async () => {
        const scope = await askScope(item, true);
        if (!scope) return;
        try {
          openEditor({ mode: 'edit', event: await api.getEvent(item.event.id), scope });
        } catch (error) {
          errorText.notify(error);
        }
      })();
    },
    copy: (item) => {
      setClipboard(item);
      toast.success(t('webmail.calendar.copyHint'));
    },
    duplicate: (item) => void createFrom(item, { start: item.grid.start, end: item.grid.end }),
    remove: (item) => {
      // Meetings confirm (the attendees are told); other events are deleted at once with Undo.
      void askScope(item).then((scope) => scope && (item.event.isMeeting ? setDeleting({ item, scope }) : undoable.remove(item.event, { scope, title: item.grid.title })));
    },
    showAs: (item, value) => void update(item, { showAs: value }),
    togglePrivate: (item) => void update(item, { sensitivity: item.event.sensitivity === 'private' ? 'normal' : 'private' }),
    respond: (item, response) => respond(item, response),
    meeting: (item, kind) => setMeetingDialog({ kind, event: item.event, title: item.grid.title }),
    saveIcs: (item) => startDownload(api.eventIcsUrl(item.event.id)),
    categorize: (item) => void askScope(item).then((scope) => scope && setCategorizing({ item, scope })),
  };
  const eventActions = createEventActions(eventActionHandlers);

  const menuFor = (id: string): ContextMenuSpec | undefined => {
    if (id === SLOT_CONTEXT_ID) return { title: t('webmail.calendar.newEvent'), actions: resolveActions(slotActions, [selection ?? nextSlot(zone)], 'context') };
    const item = byGridId(id);
    return item && { title: item.grid.title, actions: resolveActions(eventActions, [item], 'context') };
  };

  /** The full event window for the selection (or the next half hour). */
  const openFull = (slot: SlotTarget) => {
    openEditor({ mode: 'create', start: slot.start, end: slot.end, allDay: slot.allDay });
    setSelection(undefined);
  };

  /** Enter opens the event window for the selection; typing starts Quick add with that character (Outlook). */
  const onSelectionKey = (event: globalThis.KeyboardEvent) => {
    if (!selection || event.ctrlKey || event.metaKey || event.altKey) return;
    if (event.target instanceof Element && event.target.closest('input, textarea, select, [contenteditable="true"]')) return;
    if (document.querySelector('[role="dialog"], [role="alertdialog"], [role="menu"]')) return;
    if (event.key === 'Enter') {
      event.preventDefault();
      openFull(selection);
    } else if (event.key === 'Escape') setSelection(undefined);
    else if (event.key.length === 1 && event.key.trim() !== '') {
      event.preventDefault();
      setQuick({ ...selection, title: event.key });
    }
  };

  // One selection at a time; it goes away with Esc, any press outside free time (an event, a button, the rail) and
  // when the shown period or view changes. Menus and dialogs opened from it keep it until they act.
  const selectionKey = useRef<(event: globalThis.KeyboardEvent) => void>(() => undefined);
  useEffect(() => {
    selectionKey.current = onSelectionKey;
  });
  useEffect(() => {
    if (!selection) return;
    // Keys reach the window: after a click on free time nothing inside the grid has the focus.
    const onKey = (event: globalThis.KeyboardEvent) => selectionKey.current(event);
    const onPointer = (event: PointerEvent) => {
      const target = event.target instanceof Element ? event.target : null;
      if (!target || target.closest('[role="menu"], [role="dialog"], [role="alertdialog"]')) return;
      // Free time (the nearest item is a slot, not an event inside it) makes the new selection itself.
      if (target.closest('[data-context-id]')?.getAttribute('data-context-id') === SLOT_CONTEXT_ID) return;
      setPicked(undefined);
    };
    window.addEventListener('keydown', onKey);
    window.addEventListener('pointerdown', onPointer, true);
    return () => {
      window.removeEventListener('keydown', onKey);
      window.removeEventListener('pointerdown', onPointer, true);
    };
  }, [selection]);

  /** Double-click on an event opens it in the event window (read-only ones in the details panel). */
  const onGridDoubleClick = (event: MouseEvent<HTMLElement>) => {
    const id = (event.target as Element).closest('[data-context-id]')?.getAttribute('data-context-id');
    const item = id && id !== SLOT_CONTEXT_ID ? byGridId(id) : undefined;
    if (!item) return;
    event.preventDefault();
    if (editable(item)) eventActionHandlers.edit(item);
    else setSelectedId(item.grid.id);
  };

  const confirmDelete = () => {
    if (!deleting) return;
    const { item, scope } = deleting;
    undoable.remove(item.event, { scope, title: item.grid.title });
  };

  const applyOverrides = useMemo(
    () => (events: CalendarGridEvent[]) => events.map((e) => (overrides.has(e.id) ? { ...e, ...overrides.get(e.id) } : e)),
    [overrides],
  );

  const dialogs: ReactNode = (
    <>
      {choiceDialog}
      <MeetingDialogs dialog={meetingDialog} onClose={() => setMeetingDialog(undefined)} />
      {categorizing && <CategorizeDialog item={categorizing.item} scope={categorizing.scope} onClose={() => setCategorizing(undefined)} />}
      {quick && <QuickCreate request={quick} onClose={() => setQuick(undefined)} />}
      <ConfirmDialog
        open={deleting !== undefined}
        onOpenChange={(open) => !open && setDeleting(undefined)}
        title={t(deleting?.item.event.isMeeting && deleting.item.event.isOrganizer ? 'webmail.calendar.cancelTitle' : 'webmail.calendar.deleteTitle')}
        description={t(
          deleting?.item.event.isMeeting && deleting.item.event.isOrganizer ? 'webmail.calendar.cancelDescription' : deleting?.item.event.isMeeting ? 'webmail.calendar.declineDeleteDescription' : 'webmail.calendar.deleteDescription',
          { title: deleting?.item.grid.title ?? '' },
        )}
        confirmLabel={t(deleting?.item.event.isMeeting && deleting.item.event.isOrganizer ? 'webmail.calendar.cancelMeeting' : 'webmail.calendar.delete')}
        onConfirm={confirmDelete}
      />
    </>
  );

  return {
    selection,
    /** What the "New" menu acts on: the selection, else the next half hour. */
    menuSlot: selection ?? nextSlot(zone),
    selectRange: (start: Date, end: Date, allDay: boolean) => setSelection({ start, end, allDay }),
    openSelection: () => openFull(selection ?? nextSlot(zone)),
    clearSelection: () => setSelection(undefined),
    /** Quick add (Q): the title box with natural-language dates, at the selection or the next half hour. */
    quickAdd: () => setQuick({ ...(selection ?? nextSlot(zone)), title: '' }),
    canDrag,
    changeTimes: (id: string, start: Date, end: Date) => void changeTimes(id, start, end),
    applyOverrides,
    menuFor,
    onGridDoubleClick,
    slotActions,
    eventActions,
    /** For shortcuts: the same handlers as the menus. */
    eventHandlers: eventActionHandlers,
    pasteAt,
    canPaste: clipboard !== undefined,
    /** Ctrl/Alt+drag: a copy at the new time (Outlook). */
    copyTo: (id: string, start: Date, end: Date) => {
      const item = byGridId(id);
      if (item) void createFrom(item, { start, end });
    },
    blocked: (item: ShownEvent) => isBlock(item.event),
    dialogs,
  };
}
